import React from 'react';
import type { FC, FormEventHandler } from 'react';
import { Redirect, useHistory } from 'react-router';

import styled from '@emotion/styled';
import { DateTime, Duration } from 'luxon';

import { Box, Button, RadioCard, Text } from '@clutter/clean';

import { Alert } from '@shared/components/bootstrap';
import { Percent } from '@shared/components/helpers/percent';
import { FlashKind } from '@shared/types/flash';

import type { Renewal__PackageFragment } from '@portal/schema';
import { Status, useRenewalPackagesQuery, useRenewalRenewMutation } from '@portal/schema';

import { RenewalRejectButton } from './renewal_reject_button';

const Content = styled.div`
  > * + * {
    margin: 32px 0 0;
  }
`;

const Packages = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(3, 1fr);
`;

const RenewalPackageCommitment: FC<{ entry: Renewal__PackageFragment }> = ({ entry }) => {
  const duration = Duration.fromISO(entry.commitment);

  return <>{duration.toHuman()}</>;
};

const RenewalPackageTerminates: FC<{ entry: Renewal__PackageFragment }> = ({ entry }) => {
  const terminates = DateTime.fromISO(entry.terminates);

  return <>{terminates.toLocaleString(DateTime.DATE_MED)}</>;
};

const RenewalPackageName: FC<{ entry: Renewal__PackageFragment }> = ({ entry }) => (
  <>
    <Percent value={entry.percent} /> off
  </>
);

const RenewalPackageSummary: FC<{ entry: Renewal__PackageFragment }> = ({ entry }) => (
  <>
    <Percent value={entry.percent} /> off for <RenewalPackageCommitment entry={entry} />
  </>
);

const RenewalRenewForm: FC<{
  packages: Renewal__PackageFragment[];
}> = ({ packages }) => {
  const history = useHistory();
  const [execute, { loading, data }] = useRenewalRenewMutation();
  const [confirmed, setConfirmed] = React.useState(false);
  const [packageID, setPackageID] = React.useState<string>(packages[0].id);
  const selection: Renewal__PackageFragment = packages.find(({ id }) => id === packageID)!;

  const error = data?.result?.error;

  const onSubmit: FormEventHandler<HTMLFormElement> = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (loading) return;
    if (!confirmed) return;
    if (!packageID) return;

    const response = await execute({ variables: { packageID } });
    if (response.data?.result?.status !== Status.Ok) return;

    history.replace('/account', {
      flash: {
        kind: FlashKind.Success,
        message: 'Thanks! Your plan has been renewed.',
      },
    });
  };

  return (
    <form onSubmit={onSubmit}>
      <Content>
        <div>
          <Text.Title size="large">💰 Stay & Save:</Text.Title>
        </div>

        <Text.Body>
          Your storage needs are still in good hands—and we would love to keep it that way. As a valued customer, we’re
          offering you an exclusive discount when you renew your storage plan today.
        </Text.Body>

        <Packages>
          {packages.map((entry) => (
            <RadioCard
              key={entry.id}
              name="movers"
              value={entry.id}
              selected={entry.id === packageID}
              onChange={(value, event) => {
                if (event) {
                  event.preventDefault();
                  event.stopPropagation();
                }

                setPackageID(value);
              }}
            >
              <Box.Flex flexDirection="column" gap="4px" justifyContent="center" alignItems="center">
                <Text.Title size="small">
                  <RenewalPackageName entry={entry} />
                </Text.Title>
                <Text.Body>
                  <RenewalPackageSummary entry={entry} />
                </Text.Body>
              </Box.Flex>
            </RadioCard>
          ))}
        </Packages>

        <Text.Body>Here is what you get:</Text.Body>

        <ul>
          <li>
            ✅ A locked in rate until{' '}
            <strong>
              <RenewalPackageTerminates entry={selection} />
            </strong>
          </li>
          <li>
            ✅ <Percent value={selection.percent} /> off your monthly storage.
          </li>
          <li>✅ Continued access to your online inventory.</li>
          <li>✅ Flexible deliveries & pickups.</li>
        </ul>

        <Text.Body>This offer won’t last forever—renew now and keep your space at a better rate.</Text.Body>

        {error && <Alert style="danger">{error}</Alert>}

        <Text.Body>
          <label>
            <input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} /> By
            agreeing to this offer, I acknowledge that I am renewing my plan for an additional{' '}
            <RenewalPackageCommitment entry={selection} />. My new contract expires on{' '}
            <strong>
              <RenewalPackageTerminates entry={selection} />
            </strong>
            .
          </label>
        </Text.Body>

        <Button disabled={!confirmed} fullWidth loading={loading} type="submit">
          Stay and Save <Percent value={selection.percent} /> on Storage
        </Button>

        <RenewalRejectButton />
      </Content>
    </form>
  );
};

export const RenewalRenew: FC = () => {
  const { data } = useRenewalPackagesQuery();
  if (!data) return null;

  const packages = data.packages;
  if (!packages.length) return <Redirect to="/" />;

  return <RenewalRenewForm packages={packages} />;
};
